/**
 * Grouped horizontal bar chart (Recharts) – Me vs Benchmark.
 * Uses SBI colors: Benchmark = orange, Me = green.
 */

import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { useMaterialUIController } from "context";

const BAR_SIZE = 16;
const BAR_RADIUS = [0, 4, 4, 0]; // right-side rounded for horizontal bars
const LABEL_BENCHMARK = "Benchmark";
const LABEL_ME = "Me";

function GroupedBarChart({
  title,
  metrics,
  cardBg: cardBgProp,
  cardBorder: cardBorderProp,
  benchmarkColor: benchmarkColorProp,
  meColor: meColorProp,
  allColor: allColorProp,
  titleColor: titleColorProp,
  axisColor: axisColorProp,
  gridColor: gridColorProp,
  infoTooltip,
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const theme = useTheme();

  const benchmarkColor =
    benchmarkColorProp ??
    allColorProp ??
    (theme.palette?.statusSbi?.warning ||
      theme.palette?.warning?.main ||
      "#f0825c");
  const meColor =
    meColorProp ??
    theme.palette?.statusSbi?.success ??
    theme.palette?.success?.main ??
    "#35c79a";

  const cardBg =
    cardBgProp ?? (theme.palette?.surfaceSbi?.card || theme.palette?.background?.paper);
  const cardBorder =
    cardBorderProp ?? (theme.palette?.borderSbi?.subtle || theme.palette?.grey?.[600] || "rgba(0,0,0,0.12)");

  const titleColor = titleColorProp ?? "#fff";
  const axisColor = axisColorProp ?? theme.palette?.textSbi?.secondary ?? "#b6d7d7";
  const gridColor = gridColorProp ?? theme.palette?.borderSbi?.subtle ?? "rgba(255,255,255,0.15)";

  const data = metrics.map((m) => ({
    category: m.label,
    all: m.value ?? 0,
    me: m.meValue ?? 0,
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    return (
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderRadius: 1,
          border: `1px solid ${cardBorder}`,
          backgroundColor: cardBg,
          boxShadow: 2,
          minWidth: 140,
        }}
      >
        <MDTypography variant="caption" fontWeight="medium" sx={{ color: titleColor, display: "block", mb: 0.5 }}>
          {d.category}
        </MDTypography>
        <MDTypography variant="caption" sx={{ color: benchmarkColor }}>
          {LABEL_BENCHMARK}: {d.all}%
        </MDTypography>
        <MDTypography variant="caption" sx={{ color: meColor, display: "block" }}>
          {LABEL_ME}: {d.me}%
        </MDTypography>
      </Box>
    );
  };

  return (
    <Card
      sx={{
        backgroundColor: cardBg,
        border: `1px solid ${cardBorder}`,
      }}
    >
      <MDBox p={2}>
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <MDTypography
            variant="subtitle2"
            fontWeight="medium"
            textTransform="uppercase"
            sx={{
              letterSpacing: "0.5px",
              fontSize: (t) => t.typography.size.sm,
              color: titleColor,
            }}
          >
            {title}
          </MDTypography>
          <Tooltip title={infoTooltip || `${title} information`} placement="top" arrow>
            <IconButton size="small" sx={{ color: titleColor, padding: 0.5 }}>
              <Icon sx={{ fontSize: "1.25rem" }}>help_outline</Icon>
            </IconButton>
          </Tooltip>
        </MDBox>

        <Box sx={{ width: "100%", height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 10, right: 20, left: 8, bottom: 10 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={gridColor}
                horizontal={false}
              />
              <XAxis
                type="number"
                domain={[0, 100]}
                stroke={axisColor}
                tick={{ fill: axisColor, fontSize: 12 }}
                tickFormatter={(v) => `${v}%`}
              />
              <YAxis
                type="category"
                dataKey="category"
                stroke={axisColor}
                width={72}
                tick={{ fill: axisColor, fontSize: 11 }}
              />
              <RechartsTooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0,0,0,0.05)" }} />
              <Bar
                dataKey="all"
                fill={benchmarkColor}
                radius={BAR_RADIUS}
                barSize={BAR_SIZE}
                name={LABEL_BENCHMARK}
              />
              <Bar
                dataKey="me"
                fill={meColor}
                radius={BAR_RADIUS}
                barSize={BAR_SIZE}
                name={LABEL_ME}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>

        {/* Legend – centered at bottom like v0 */}
        <MDBox display="flex" justifyContent="center" gap={3} mt={2}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: 0.5,
                backgroundColor: meColor,
              }}
            />
            <MDTypography variant="caption" sx={{ color: axisColor }}>
              {LABEL_ME}
            </MDTypography>
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: 0.5,
                backgroundColor: benchmarkColor,
              }}
            />
            <MDTypography variant="caption" sx={{ color: axisColor }}>
              {LABEL_BENCHMARK}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}

GroupedBarChart.defaultProps = {
  title: "Metrics",
  metrics: [],
  cardBg: undefined,
  cardBorder: undefined,
  benchmarkColor: undefined,
  meColor: undefined,
  allColor: undefined,
  titleColor: undefined,
  axisColor: undefined,
  gridColor: undefined,
  infoTooltip: undefined,
};

GroupedBarChart.propTypes = {
  title: PropTypes.string,
  metrics: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      meValue: PropTypes.number,
    })
  ),
  cardBg: PropTypes.string,
  cardBorder: PropTypes.string,
  benchmarkColor: PropTypes.string,
  meColor: PropTypes.string,
  allColor: PropTypes.string,
  titleColor: PropTypes.string,
  axisColor: PropTypes.string,
  gridColor: PropTypes.string,
  infoTooltip: PropTypes.string,
};

export default GroupedBarChart;
